/**
 * FocusContext — Shared state for Focus Mode sessions.
 *
 * Provides timer state, current plan, and session data that persists across
 * route changes so the FocusWidget can render on any page. Timer uses
 * Date.now() comparison so background tabs don't drift.
 *
 * Reads/writes focus settings from the user doc (focusSettings field).
 * Active session data persisted to localStorage for crash recovery.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { db } from '../firebase';
import {
  addDoc, collection, doc, getDoc, serverTimestamp, setDoc, updateDoc,
} from 'firebase/firestore';
import { withTenant } from '../services/data';

/* ── localStorage keys ── */
const LS_TIMER = 'focusTimer';
const LS_SESSION = 'focusSession';

/* ── Defaults ── */
const DEFAULT_SETTINGS = {
  workDuration: 25,
  shortBreak: 5,
  longBreak: 15,
  sessionsBeforeLongBreak: 4,
  soundEnabled: false,
  autoStartBreaks: true,
  maxPlanItems: 7,
};

/* ── Helpers ── */
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function loadJSON(key) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : null; } catch { return null; }
}
function saveJSON(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* */ }
}
function clearLS(key) {
  try { localStorage.removeItem(key); } catch { /* */ }
}

/**
 * RW-3538: whole minutes of the work interval still open when a session ends.
 * Focus time was credited only when a pomodoro ran to zero, so a session ended
 * part-way through its first one saved 0 minutes — and the Focus leaderboard
 * showed "0m · 5 sessions". A break in progress adds nothing; a timer that
 * never started (remaining = the full interval) adds nothing.
 */
export function openWorkMinutes({ sessionType, isRunning, timerStartedAt, remaining, workSeconds, now = Date.now() }) {
  if (sessionType !== 'work' || !(workSeconds > 0)) return 0;
  const elapsed = isRunning && timerStartedAt
    ? (now - timerStartedAt) / 1000
    : workSeconds - (Number(remaining) || 0);
  return Math.floor(Math.max(0, Math.min(workSeconds, elapsed)) / 60);
}

/* ── Context ── */
const FocusContext = createContext(null);

export function useFocus() {
  const ctx = useContext(FocusContext);
  if (!ctx) throw new Error('useFocus must be used inside FocusProvider');
  return ctx;
}

export function FocusProvider({ user, children }) {
  const uid = user?.uid;

  /* ── Settings (from Firestore user doc) ── */
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  useEffect(() => {
    if (!uid) return;
    getDoc(doc(db, 'users', uid)).then(snap => {
      const fs = snap.data()?.focusSettings;
      if (fs) setSettings(prev => ({ ...prev, ...fs }));
    }).catch(() => {});
  }, [uid]);

  const updateSettings = useCallback(async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    if (uid) {
      await updateDoc(doc(db, 'users', uid), { focusSettings: next }).catch(() => {});
    }
  }, [settings, uid]);

  /* ── Session state ── */
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [plannedItems, setPlannedItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pomodorosCompleted, setPomodorosCompleted] = useState(0);
  const [totalFocusMinutes, setTotalFocusMinutes] = useState(0);
  const [totalBreakMinutes, setTotalBreakMinutes] = useState(0);
  const [distractionCount, setDistractionCount] = useState(0);
  const [sessionStartedAt, setSessionStartedAt] = useState(null);

  /* ── Timer state ── */
  // sessionType: 'work' | 'shortBreak' | 'longBreak'
  const [sessionType, setSessionType] = useState('work');
  const [remaining, setRemaining] = useState(settings.workDuration * 60); // seconds
  const [isRunning, setIsRunning] = useState(false);
  const [timerStartedAt, setTimerStartedAt] = useState(null); // Date.now() when started
  const intervalRef = useRef(null);
  const [sessionNumber, setSessionNumber] = useState(1); // 1-based, cycles through sessionsBeforeLongBreak

  /* ── Derive duration from type ── */
  const durationForType = useCallback((type) => {
    if (type === 'shortBreak') return settings.shortBreak * 60;
    if (type === 'longBreak') return settings.longBreak * 60;
    return settings.workDuration * 60;
  }, [settings]);

  /* ── Timer tick — drift-corrected via Date.now() ── */
  useEffect(() => {
    if (!isRunning || !timerStartedAt) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    const totalSec = durationForType(sessionType);
    intervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - timerStartedAt) / 1000);
      const left = Math.max(0, totalSec - elapsed);
      setRemaining(left);
      if (left <= 0) {
        clearInterval(intervalRef.current);
        setIsRunning(false);
        // Timer completed — the effect below reacts to remaining === 0
      }
    }, 250); // 4x/sec for smooth display
    return () => clearInterval(intervalRef.current);
  }, [isRunning, timerStartedAt, sessionType, durationForType]);

  /* ── Timer completion handler ── */
  const [timerJustCompleted, setTimerJustCompleted] = useState(null); // 'work' | 'shortBreak' | 'longBreak' | null

  useEffect(() => {
    if (remaining === 0 && !isRunning && timerStartedAt) {
      setTimerJustCompleted(sessionType);
      setTimerStartedAt(null);
    }
  }, [remaining, isRunning, timerStartedAt, sessionType]);

  /* ── Process timer completion ── */
  useEffect(() => {
    if (!timerJustCompleted) return;
    const type = timerJustCompleted;
    setTimerJustCompleted(null);

    if (type === 'work') {
      const mins = settings.workDuration;
      setTotalFocusMinutes(prev => prev + mins);
      const newPoms = pomodorosCompleted + 1;
      setPomodorosCompleted(newPoms);

      // Determine next break type
      const isLongBreak = newPoms % settings.sessionsBeforeLongBreak === 0;
      const nextType = isLongBreak ? 'longBreak' : 'shortBreak';
      setSessionType(nextType);
      setRemaining(durationForType(nextType));
      setSessionNumber(prev => prev < settings.sessionsBeforeLongBreak ? prev + 1 : 1);

      if (settings.autoStartBreaks) {
        startTimer(nextType);
      }
    } else {
      // Break completed — back to work
      const mins = type === 'longBreak' ? settings.longBreak : settings.shortBreak;
      setTotalBreakMinutes(prev => prev + mins);
      setSessionType('work');
      setRemaining(durationForType('work'));
    }

    // Persist to localStorage
    saveTimerState();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerJustCompleted]);

  /* ── Timer controls ── */
  const startTimer = useCallback((type) => {
    const t = type || sessionType;
    const dur = durationForType(t);
    setSessionType(t);
    setRemaining(dur);
    setTimerStartedAt(Date.now());
    setIsRunning(true);
  }, [sessionType, durationForType]);

  const pauseTimer = useCallback(() => {
    if (!isRunning) return;
    // Save remaining time, stop
    const elapsed = Math.floor((Date.now() - timerStartedAt) / 1000);
    const totalSec = durationForType(sessionType);
    const left = Math.max(0, totalSec - elapsed);
    setRemaining(left);
    setIsRunning(false);
    setTimerStartedAt(null);
  }, [isRunning, timerStartedAt, sessionType, durationForType]);

  const resumeTimer = useCallback(() => {
    if (isRunning || remaining <= 0) return;
    // Recalculate timerStartedAt so the remaining time is correct
    const totalSec = durationForType(sessionType);
    const elapsed = totalSec - remaining;
    setTimerStartedAt(Date.now() - elapsed * 1000);
    setIsRunning(true);
  }, [isRunning, remaining, sessionType, durationForType]);

  const resetTimer = useCallback(() => {
    setIsRunning(false);
    setTimerStartedAt(null);
    setRemaining(durationForType(sessionType));
  }, [sessionType, durationForType]);

  const toggleTimer = useCallback(() => {
    if (isRunning) pauseTimer();
    else resumeTimer();
  }, [isRunning, pauseTimer, resumeTimer]);

  /* ── Save/restore timer state for crash recovery ── */
  const saveTimerState = useCallback(() => {
    saveJSON(LS_TIMER, { remaining, isRunning, sessionType, timerStartedAt, sessionNumber });
  }, [remaining, isRunning, sessionType, timerStartedAt, sessionNumber]);

  const saveSessionState = useCallback(() => {
    if (!sessionActive) return;
    saveJSON(LS_SESSION, {
      sessionId, plannedItems, currentIndex, pomodorosCompleted,
      totalFocusMinutes, totalBreakMinutes, distractionCount, sessionStartedAt,
      sessionNumber,
    });
  }, [sessionActive, sessionId, plannedItems, currentIndex, pomodorosCompleted,
      totalFocusMinutes, totalBreakMinutes, distractionCount, sessionStartedAt, sessionNumber]);

  // Auto-save on state changes
  useEffect(() => { if (sessionActive) saveTimerState(); }, [sessionActive, saveTimerState]);
  useEffect(() => { if (sessionActive) saveSessionState(); }, [sessionActive, saveSessionState]);

  /* ── Start a focus session ── */
  const startSession = useCallback(async (items) => {
    const now = Date.now();
    const planned = items.map((item, i) => ({
      ...item,
      status: i === 0 ? 'in_progress' : 'planned',
      completedAt: null,
    }));

    // Create Firestore doc
    let docId = null;
    if (uid) {
      try {
        const ref = await addDoc(collection(db, 'users', uid, 'focusSessions'), withTenant(`users/${uid}/focusSessions`, {
          date: todayStr(),
          startedAt: serverTimestamp(),
          endedAt: null,
          totalFocusMinutes: 0,
          totalBreakMinutes: 0,
          pomodorosCompleted: 0,
          plannedItems: planned,
          completedCount: 0,
          skippedCount: 0,
          distractionCount: 0,
          settings: {
            workDuration: settings.workDuration,
            shortBreak: settings.shortBreak,
            longBreak: settings.longBreak,
            sessionsBeforeLongBreak: settings.sessionsBeforeLongBreak,
          },
        }));
        docId = ref.id;
      } catch (err) {
        console.error('[Focus] Failed to create session doc:', err);
      }
    }

    setSessionId(docId);
    setPlannedItems(planned);
    setCurrentIndex(0);
    setPomodorosCompleted(0);
    setTotalFocusMinutes(0);
    setTotalBreakMinutes(0);
    setDistractionCount(0);
    setSessionStartedAt(now);
    setSessionNumber(1);
    setSessionType('work');
    setRemaining(settings.workDuration * 60);
    setIsRunning(false);
    setTimerStartedAt(null);
    setSessionActive(true);
  }, [uid, settings]);

  /* ── Complete current item ── */
  const completeCurrentItem = useCallback(() => {
    setPlannedItems(prev => {
      const next = [...prev];
      if (next[currentIndex]) {
        next[currentIndex] = { ...next[currentIndex], status: 'completed', completedAt: Date.now() };
      }
      return next;
    });
    // Advance to next non-completed item
    const nextIdx = plannedItems.findIndex((item, i) => i > currentIndex && item.status !== 'completed' && item.status !== 'skipped');
    if (nextIdx >= 0) {
      setCurrentIndex(nextIdx);
      setPlannedItems(prev => {
        const next = [...prev];
        next[nextIdx] = { ...next[nextIdx], status: 'in_progress' };
        return next;
      });
    }
  }, [currentIndex, plannedItems]);

  /* ── Skip current item ── */
  const skipCurrentItem = useCallback(() => {
    setPlannedItems(prev => {
      const next = [...prev];
      if (next[currentIndex]) {
        next[currentIndex] = { ...next[currentIndex], status: 'skipped' };
      }
      return next;
    });
    const nextIdx = plannedItems.findIndex((item, i) => i > currentIndex && item.status !== 'completed' && item.status !== 'skipped');
    if (nextIdx >= 0) {
      setCurrentIndex(nextIdx);
      setPlannedItems(prev => {
        const next = [...prev];
        next[nextIdx] = { ...next[nextIdx], status: 'in_progress' };
        return next;
      });
    }
  }, [currentIndex, plannedItems]);

  /* ── Log distraction ── */
  const logDistraction = useCallback(() => {
    setDistractionCount(prev => prev + 1);
  }, []);

  /* ── End session ── */
  const endSession = useCallback(async () => {
    // Pause timer
    setIsRunning(false);
    setTimerStartedAt(null);

    const completedCount = plannedItems.filter(i => i.status === 'completed').length;
    const skippedCount = plannedItems.filter(i => i.status === 'skipped').length;
    // RW-3538: the finished pomodoros plus the one still running.
    const sessionFocusMinutes = totalFocusMinutes + openWorkMinutes({
      sessionType, isRunning, timerStartedAt, remaining, workSeconds: durationForType('work'),
    });

    // Update Firestore session doc
    if (uid && sessionId) {
      try {
        await updateDoc(doc(db, 'users', uid, 'focusSessions', sessionId), {
          endedAt: serverTimestamp(),
          totalFocusMinutes: sessionFocusMinutes,
          totalBreakMinutes,
          pomodorosCompleted,
          plannedItems,
          completedCount,
          skippedCount,
          distractionCount,
        });
      } catch (err) {
        console.error('[Focus] Failed to update session:', err);
      }

      // Update focus stats
      try {
        const statsRef = doc(db, 'users', uid, 'stats', 'focusStats');
        const statsSnap = await getDoc(statsRef);
        const prev = statsSnap.exists() ? statsSnap.data() : {};
        const today = todayStr();
        const lastDate = prev.lastSessionDate || '';
        const yesterday = new Date(Date.now() - 86400000);
        const yesterdayStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;

        let currentStreak = prev.currentStreak || 0;
        if (lastDate === today) {
          // Same day — no streak change
        } else if (lastDate === yesterdayStr) {
          currentStreak += 1;
        } else {
          currentStreak = 1;
        }

        const totalSessions = (prev.totalSessions || 0) + 1;
        const totalMins = (prev.totalFocusMinutes || 0) + sessionFocusMinutes;
        const totalTasks = (prev.totalTasksCompleted || 0) + completedCount;
        const longestStreak = Math.max(prev.longestStreak || 0, currentStreak);
        const rate = totalSessions > 0
          ? Math.round(totalTasks / totalSessions * plannedItems.length / Math.max(1, completedCount + skippedCount) * 100)
          : 0;

        await setDoc(statsRef, withTenant(`users/${uid}/stats`, {
          totalSessions,
          totalFocusMinutes: totalMins,
          totalTasksCompleted: totalTasks,
          currentStreak,
          longestStreak,
          lastSessionDate: today,
          avgSessionMinutes: Math.round(totalMins / totalSessions),
          avgTasksPerSession: Math.round(totalTasks / totalSessions * 10) / 10,
          taskCompletionRate: Math.min(100, rate),
        }), { merge: true });
      } catch (err) {
        console.error('[Focus] Failed to update stats:', err);
      }
    }

    const summary = {
      totalFocusMinutes: sessionFocusMinutes,
      totalBreakMinutes,
      pomodorosCompleted,
      completedCount,
      skippedCount,
      plannedCount: plannedItems.length,
      distractionCount,
      items: plannedItems,
    };

    // Clear state
    setSessionActive(false);
    setSessionId(null);
    setPlannedItems([]);
    setCurrentIndex(0);
    setPomodorosCompleted(0);
    setTotalFocusMinutes(0);
    setTotalBreakMinutes(0);
    setDistractionCount(0);
    setSessionStartedAt(null);
    clearLS(LS_TIMER);
    clearLS(LS_SESSION);

    return summary;
  }, [uid, sessionId, plannedItems, totalFocusMinutes, totalBreakMinutes, pomodorosCompleted, distractionCount, sessionType, isRunning, timerStartedAt, remaining, durationForType]);

  /* ── Restore session from localStorage on mount ── */
  useEffect(() => {
    const saved = loadJSON(LS_SESSION);
    if (!saved || !saved.plannedItems?.length) return;

    setSessionActive(true);
    setSessionId(saved.sessionId);
    setPlannedItems(saved.plannedItems);
    setCurrentIndex(saved.currentIndex || 0);
    setPomodorosCompleted(saved.pomodorosCompleted || 0);
    setTotalFocusMinutes(saved.totalFocusMinutes || 0);
    setTotalBreakMinutes(saved.totalBreakMinutes || 0);
    setDistractionCount(saved.distractionCount || 0);
    setSessionStartedAt(saved.sessionStartedAt);
    setSessionNumber(saved.sessionNumber || 1);

    const timerState = loadJSON(LS_TIMER);
    if (timerState) {
      setSessionType(timerState.sessionType || 'work');
      setRemaining(timerState.remaining || 0);
      setSessionNumber(timerState.sessionNumber || 1);
      // Don't auto-resume — ask user
    }
  }, []);

  /* ── Current item ── */
  const currentItem = useMemo(() => {
    if (!sessionActive || currentIndex >= plannedItems.length) return null;
    return plannedItems[currentIndex];
  }, [sessionActive, currentIndex, plannedItems]);

  const completedCount = useMemo(() => plannedItems.filter(i => i.status === 'completed').length, [plannedItems]);
  const allDone = useMemo(() => {
    if (!plannedItems.length) return false;
    return plannedItems.every(i => i.status === 'completed' || i.status === 'skipped');
  }, [plannedItems]);

  /* ── Progress fraction for timer ring ── */
  const timerProgress = useMemo(() => {
    const total = durationForType(sessionType);
    if (total === 0) return 0;
    return 1 - remaining / total;
  }, [remaining, sessionType, durationForType]);

  const value = useMemo(() => ({
    // Settings
    settings, updateSettings,
    // Session
    sessionActive, sessionId, plannedItems, setPlannedItems,
    currentIndex, setCurrentIndex, currentItem,
    pomodorosCompleted, totalFocusMinutes, totalBreakMinutes,
    distractionCount, sessionStartedAt, completedCount, allDone,
    // Timer
    sessionType, remaining, isRunning, timerProgress,
    sessionNumber, timerJustCompleted,
    // Timer controls
    startTimer, pauseTimer, resumeTimer, resetTimer, toggleTimer,
    // Session controls
    startSession, completeCurrentItem, skipCurrentItem, logDistraction, endSession,
    // Helpers
    durationForType,
  }), [
    settings, updateSettings,
    sessionActive, sessionId, plannedItems, setPlannedItems,
    currentIndex, setCurrentIndex, currentItem,
    pomodorosCompleted, totalFocusMinutes, totalBreakMinutes,
    distractionCount, sessionStartedAt, completedCount, allDone,
    sessionType, remaining, isRunning, timerProgress,
    sessionNumber, timerJustCompleted,
    startTimer, pauseTimer, resumeTimer, resetTimer, toggleTimer,
    startSession, completeCurrentItem, skipCurrentItem, logDistraction, endSession,
    durationForType,
  ]);

  return (
    <FocusContext.Provider value={value}>
      {children}
    </FocusContext.Provider>
  );
}
